import { useEffect } from 'react';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import {
  Flex,
  Button,
  Input,
  CardBody,
  Card,
  FormControl,
  Text,
  useDisclosure,
  FormLabel,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import {
  Concept,
  conceptsApi,
  UpdateConceptRequest,
} from '@texas/api/endpoints/conceptsApi';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { useForm } from 'react-hook-form';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useToast } from '@chakra-ui/react';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { reactEvents } from '@bridge/reactEvents';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';

interface Props {
  concept: Concept;
}

export function ConceptManagement({ concept }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, loading, refetch, set } = useApiResource(conceptsApi.get);

  const {
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useDisclosure();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting, errors },
  } = useForm<UpdateConceptRequest>({
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    refetch(concept.id);
  }, [refetch, concept.id]);

  useEffect(() => {
    if (!data) return;
    reset({ name: data.name });
  }, [data, reset]);

  const { request: updateConceptReq, error: updateConceptError } =
    useApiRequest(conceptsApi.update);

  const onSubmit = async (data: UpdateConceptRequest) => {
    await request(
      updateConceptReq,
      [concept.id, { name: data.name }],
      (response) => {
        toast({
          title: t('general.updated'),
          status: 'success',
          isClosable: true,
        });

        set(response);

        reactEvents.conceptNameChanged.dispatch({
          id: response.id,
          value: response.name,
        });
      },
    );
  };

  return (
    <BodyOverride>
      <Card variant="dark">
        <CardBody>
          <Text variant="header-medium" mb={6}>
            {t('concept.management')}
          </Text>
          {loading && <SpinnerWithLabel label={t('general.loadingData')} />}
          {updateConceptError && <ErrorDetails error={updateConceptError} />}
          {data && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Flex gap={4} direction="column" mb={4}>
                <FormControl isRequired={true}>
                  <FormLabel>{t('general.name')}</FormLabel>
                  <Input
                    {...register('name', {
                      required: {
                        value: true,
                        message: t('errors.nameRequired', {
                          name: t('general.name'),
                        }),
                      },
                      maxLength: {
                        value: 100,
                        message: t('errors.maxLength', { count: 100 }),
                      },
                    })}
                    variant="texas"
                  />
                  <ErrorLabel text={errors.name?.message} />
                </FormControl>
                <Flex gap={2} ml="auto" w="fit-content">
                  <Button
                    w="100%"
                    variant="texas-light"
                    onClick={onArchiveOpen}
                  >
                    {data.archived
                      ? t('general.restore')
                      : t('general.archive', { name: data.name })}
                  </Button>
                  <SubmitButton disabled={!isDirty} loading={isSubmitting}>
                    {t('general.save')}
                  </SubmitButton>
                </Flex>
              </Flex>
            </form>
          )}
        </CardBody>
      </Card>
      {data && (
        <VerifyDialogWithRequest
          headerTitle={
            data.archived
              ? t('general.restore')
              : t('configuration.archive', { name: data.name })
          }
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={
            data.archived ? conceptsApi.restore : conceptsApi.archive
          }
          args={[concept.id]}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onPerformed={(response) => {
            set((prev) =>
              prev ? { ...prev, archived: response.archived } : null,
            );
            if (response.archived) {
              reactEvents.conceptArchived.dispatch(concept.id);
            } else {
              reactEvents.conceptRestored.dispatch(concept.id);
            }
            onArchiveClose();
          }}
          onSuccessTitle={
            data.archived
              ? t('general.entityRestored', { name: data.name })
              : t('general.entityArchived', { name: data.name })
          }
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
      )}
    </BodyOverride>
  );
}
